import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import BinderBrowser from '../Binders/BinderBrowser';
import Tabs from '../../common/Tabs/Tabs';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import LoginGate from '../../common/LoginGate/LoginGate';
import { levelFromXp, borderForUser } from '../../../utils/experience';
import Icon from '../../common/Icon/Icon';
import './Profile.css';

const REPORT_REASONS = [
    ['scam', 'Scam or fraud'],
    ['no-show', "Didn't ship / no-show"],
    ['harassment', 'Harassment'],
    ['fake-binder', 'Fake binder or listings'],
    ['other', 'Something else'],
];

function Profile() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser, loading: userLoading } = useUser();

    const [profileUser, setProfileUser] = useState(null);
    const [binders, setBinders] = useState({ haves: [], wants: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOwnProfile, setIsOwnProfile] = useState(false);
    const [activeTab, setActiveTab] = useState('collection');

    // Report dialog
    const [reportOpen, setReportOpen] = useState(false);
    const [reportReason, setReportReason] = useState('scam');
    const [reportDetail, setReportDetail] = useState('');
    const [reportBusy, setReportBusy] = useState(false);
    const [reportNote, setReportNote] = useState(null);

    // Blocking: 'by_me' / 'by_them' / null comes with the profile
    const [blockAsk, setBlockAsk] = useState(false);
    const [blockBusy, setBlockBusy] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const toggleBlock = async (block) => {
        setBlockBusy(true);
        try {
            const res = await fetch(`/api/blocks/${profileUser.id}`, {
                method: block ? 'POST' : 'DELETE',
                credentials: 'include',
            });
            if (!res.ok) throw new Error((await res.json()).error || 'That didn’t work.');
            setReloadKey(k => k + 1); // reload profile + binders in their new state
        } catch (e) {
            setReportNote(e.message);
        } finally {
            setBlockBusy(false);
        }
    };

    const fileReport = async () => {
        setReportBusy(true);
        try {
            const res = await fetch(`/api/users/${profileUser.id}/report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason: reportReason, detail: reportDetail.trim() }),
            });
            const d = await res.json();
            setReportNote(res.ok ? 'Report filed. An admin will review it.' : (d.error || 'Failed to file the report.'));
        } catch {
            setReportNote('Failed to file the report.');
        } finally {
            setReportBusy(false);
            setReportOpen(false);
            setReportDetail('');
        }
    };

    // If this user runs an active verified store, link to its store page
    const [ownedStore, setOwnedStore] = useState(null);
    useEffect(() => {
        setOwnedStore(null);
        const targetId = userId || currentUser?.id;
        if (!targetId) return;
        fetch(`/api/shops/by-owner/${targetId}`)
            .then(r => (r.ok ? r.json() : null))
            .then(d => setOwnedStore(d ? d.shop : null))
            .catch(() => { });
    }, [userId, currentUser?.id]);

    // Show Matches state - auto-enabled when viewing others' profiles
    const [showMatches, setShowMatches] = useState(true);
    const [currentUserCollection, setCurrentUserCollection] = useState([]);
    const [currentUserWishlist, setCurrentUserWishlist] = useState([]);

    // Fetch current user's collection and wishlist when showMatches is enabled
    useEffect(() => {
        const fetchCurrentUserData = async () => {
            if (showMatches && !isOwnProfile && currentUser?.id) {
                try {
                    // Fetch collection entries
                    const collectionResponse = await fetch(`/api/haves/${currentUser.id}`, { credentials: 'include' });
                    let collectionWithNames = [];
                    if (collectionResponse.ok) {
                        const collectionData = await collectionResponse.json();
                        const haves = collectionData.haves || [];
                        
                        // Fetch card details to get names for "any" finish matching
                        if (haves.length > 0) {
                            const entries = haves.map(h => ({ uuid: h.uuid, setcode: h.setcode }));
                            const cardRes = await fetch('/api/cards/by-uuids-with-setcodes', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ entries })
                            });
                            if (cardRes.ok) {
                                const cardData = await cardRes.json();
                                collectionWithNames = haves.map(h => {
                                    const card = cardData.cards.find(c => c.uuid === h.uuid);
                                    return { ...h, name: card?.name || null };
                                });
                            } else {
                                collectionWithNames = haves;
                            }
                        }
                    }
                    setCurrentUserCollection(collectionWithNames);

                    // Fetch wishlist entries
                    const wishlistResponse = await fetch(`/api/wants/${currentUser.id}`, { credentials: 'include' });
                    let wishlistWithNames = [];
                    if (wishlistResponse.ok) {
                        const wishlistData = await wishlistResponse.json();
                        const wants = wishlistData.wants || [];
                        
                        // Fetch card details to get names for "any" finish matching
                        if (wants.length > 0) {
                            const entries = wants.map(w => ({ uuid: w.uuid, setcode: w.setcode }));
                            const cardRes = await fetch('/api/cards/by-uuids-with-setcodes', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ entries })
                            });
                            if (cardRes.ok) {
                                const cardData = await cardRes.json();
                                wishlistWithNames = wants.map(w => {
                                    const card = cardData.cards.find(c => c.uuid === w.uuid);
                                    return { ...w, name: card?.name || null };
                                });
                            } else {
                                wishlistWithNames = wants;
                            }
                        }
                    }
                    setCurrentUserWishlist(wishlistWithNames);
                } catch (err) {
                    console.error('Error fetching current user data:', err);
                }
            }
        };
        fetchCurrentUserData();
    }, [showMatches, isOwnProfile, currentUser?.id]);

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                setLoading(true);
                setError(null);

                const targetUserId = userId || currentUser?.id;
                const isOwn = !userId || userId === currentUser?.id?.toString();
                setIsOwnProfile(isOwn);

                const userResponse = await fetch(`/api/users/${targetUserId}/profile`, {
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (!userResponse.ok) throw new Error('Failed to fetch user profile');
                const userData = await userResponse.json();
                setProfileUser(userData);

                // Across a block there are no binders to show
                if (userData.blocked) {
                    setBinders({ haves: [], wants: [] });
                    return;
                }
                const bindersResponse = await fetch(`/api/users/${targetUserId}/binders`, {
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (!bindersResponse.ok) throw new Error('Failed to fetch binders data');
                const bindersData = await bindersResponse.json();
                setBinders(bindersData);
            } catch (err) {
                console.error('Error fetching profile data:', err);
                setError('Failed to load profile data');
            } finally {
                setLoading(false);
            }
        };

        if (currentUser) {
            fetchProfileData();
        }
    }, [userId, currentUser, reloadKey]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Get current cards based on active tab
    const currentCards = useMemo(() => {
        const cards = activeTab === 'collection' ? binders.haves : binders.wants;
        // The {card, binder} shape the binder browser uses
        return cards.map(item => ({ card: item.card, binder: item.entry }));
    }, [activeTab, binders]);

    // Get matched card keys and names for highlighting
    // Returns { keys: Set of uuid-setcode, names: Set of lowercase card names (for 'any' finish matching) }
    const matchedCardData = useMemo(() => {
        if (!showMatches || isOwnProfile) return { keys: new Set(), names: new Set() };

        const keys = new Set();
        const names = new Set();
        
        if (activeTab === 'collection') {
            // Profile user's collection - match against current user's wishlist
            currentUserWishlist.forEach(want => {
                keys.add(`${want.uuid}-${want.setcode}`);
                // For 'any' finish, also match by card name
                if (want.finish === 'any' && want.name) {
                    names.add(want.name.trim().toLowerCase());
                }
            });
        } else {
            // Profile user's wishlist - match against current user's collection
            currentUserCollection.forEach(have => {
                keys.add(`${have.uuid}-${have.setcode}`);
                // For 'any' finish items in their wishlist, match by name from our collection
                if (have.name) {
                    names.add(have.name.trim().toLowerCase());
                }
            });
        }
        return { keys, names };
    }, [showMatches, isOwnProfile, activeTab, currentUserCollection, currentUserWishlist]);
    
    // A card fits your binder: same printing, or (any-printing wishes)
    // the same card name
    const isMatched = useCallback(card =>
        matchedCardData.keys.has(`${card.uuid}-${card.setCode}`) ||
        (!!card.name && matchedCardData.names.has(card.name.trim().toLowerCase())),
    [matchedCardData]);

    // Profiles are members-only. Without this, a logged-out visitor sat on
    // "Loading profile..." forever, because the load only runs for members.
    if (!userLoading && !currentUser) {
        return <LoginGate title="Profile" message="Log in to view trader profiles." />;
    }

    if (loading) {
        return (
            <div className="profile-container">
                <div className="profile-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-container">
                <div className="profile-error">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button className="btn-primary" onClick={() => navigate(-1)}>Go Back</button>
                </div>
            </div>
        );
    }

    if (!profileUser) {
        return (
            <div className="profile-container">
                <div className="profile-error">
                    <h2>Profile Not Found</h2>
                    <p>The requested profile could not be found.</p>
                    <button className="btn-primary" onClick={() => navigate(-1)}>Go Back</button>
                </div>
            </div>
        );
    }

    const level = levelFromXp(profileUser.experience);
    const border = borderForUser(profileUser);
    const trades = profileUser.trades || { middleman: 0, direct: 0 };
    const blockState = isOwnProfile ? null : profileUser.blocked || null;
    const binderNoun = activeTab === 'collection' ? 'collection' : 'wishlist';

    return (
        <div className="profile-container">
            <ConfirmDialog
                open={reportOpen}
                title={`Report ${profileUser.username}`}
                confirmLabel="File report"
                danger
                busy={reportBusy}
                onCancel={() => setReportOpen(false)}
                onConfirm={fileReport}
                message={(
                    <div className="pro-report-form">
                        <label>
                            What happened?
                            <select value={reportReason} onChange={e => setReportReason(e.target.value)}>
                                {REPORT_REASONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                            </select>
                        </label>
                        <textarea
                            placeholder="Optional: anything an admin should know (trade code, what was said…)"
                            maxLength={500}
                            rows={3}
                            value={reportDetail}
                            onChange={e => setReportDetail(e.target.value)}
                        />
                    </div>
                )}
            />
            {/* Profile Header: identity left, trade record right */}
            <div className="profile-header pro-head">
                <AvatarFrame user={profileUser}>
                    <img
                        src={getProfilePhotoSrc(profileUser)}
                        alt={`${profileUser.username}'s profile`}
                        className="profile-avatar"
                        onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                    />
                </AvatarFrame>

                <div className="pro-main">
                    <div className="pro-namerow">
                        <h1 className="profile-username">{profileUser.username}</h1>
                        {profileUser.is_banned ? (
                            <span className="pro-level-chip pro-chip-banned">Banned</span>
                        ) : (
                            <span className={`pro-level-chip pro-chip-${border.key}`} title="Earned through middleman trades">
                                {profileUser.role === 'admin' ? 'Admin'
                                    : profileUser.role === 'shop' ? 'Verified Shop'
                                        : `Level ${level}`}
                            </span>
                        )}
                    </div>

                    <p className="pro-meta">
                        {profileUser.location_text && <><Icon name="pin" /> {profileUser.location_text} · </>}
                        Joined {formatDate(profileUser.created_at)}
                        {profileUser.is_email_verified && <> · <span className="pro-verified">✓ email verified</span></>}
                    </p>

                    {(ownedStore || (profileUser.mm_default && profileUser.mm_default !== 'ask')) && (
                        <div className="pro-badges">
                            {ownedStore && (
                                <button
                                    className="profile-store-badge"
                                    onClick={() => navigate(`/store/${ownedStore.id}`)}
                                    title="Visit their store page"
                                >
                                    ◆ Runs {ownedStore.name} · view store
                                </button>
                            )}
                            {profileUser.mm_default && profileUser.mm_default !== 'ask' && (
                                <span
                                    className={`profile-mm-badge ${profileUser.mm_default}`}
                                    title="Their default at the shipping middleman step"
                                >
                                    {profileUser.mm_default === 'yes'
                                        ? <><Icon name="shield" /> Prefers middleman trades</>
                                        : <><Icon name="mail" /> Prefers direct trades</>}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="profile-actions">
                        {isOwnProfile ? (
                            <button className="edit-profile-btn" onClick={() => navigate('/account')}>
                                Edit Profile
                            </button>
                        ) : blockState ? (
                            // Blocked either way: no trading, no chatting
                            blockState === 'by_me' && (
                                <button className="pro-report-btn" onClick={() => toggleBlock(false)} disabled={blockBusy}>
                                    Unblock
                                </button>
                            )
                        ) : (
                            <>
                                <button
                                    className="send-trade-btn"
                                    onClick={() => navigate('/trade', { state: { ownerId: profileUser.id } })}
                                >
                                    Send Trade Request
                                </button>
                                <button className="pro-message-btn" onClick={() => navigate(`/messages?to=${profileUser.id}`)}>
                                    <Icon name="chat" /> Message
                                </button>
                                <button className="pro-report-btn" onClick={() => { setReportNote(null); setReportOpen(true); }}>
                                    Report
                                </button>
                                <button className="pro-report-btn" onClick={() => setBlockAsk(true)} disabled={blockBusy}>
                                    Block
                                </button>
                            </>
                        )}
                    </div>
                    {reportNote && <p className="pro-meta">{reportNote}</p>}
                </div>

                {/* The trade record - completed trades, by how they shipped */}
                <div className="pro-record">
                    <div className="pro-stat" title="Completed trades verified by a middleman shop">
                        <span className="pro-stat-n">{trades.middleman}</span>
                        <span className="pro-stat-l"><Icon name="shield" /> middleman trade{trades.middleman !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="pro-stat" title="Completed trades shipped directly">
                        <span className="pro-stat-n">{trades.direct}</span>
                        <span className="pro-stat-l"><Icon name="mail" /> direct trade{trades.direct !== 1 ? 's' : ''}</span>
                    </div>
                </div>
            </div>

            {/* Binders Section (none across a block) */}
            {blockState ? (
                <div className="dash-sec-empty bn-empty profile-blocked">
                    <p>
                        {blockState === 'by_me'
                            ? `You blocked ${profileUser.username}. Unblock them to see their binders, message or trade.`
                            : "This trader isn't available."}
                    </p>
                </div>
            ) : (
            <div className="profile-binders">
                <Tabs
                    variant="primary"
                    active={activeTab}
                    onChange={setActiveTab}
                    items={[
                        { id: 'collection', label: `Collection (${binders.haves?.length || 0})`, shortLabel: 'Collection' },
                        { id: 'wishlist', label: `Wishlist (${binders.wants?.length || 0})`, shortLabel: 'Wishlist' },
                    ]}
                />

                {/* Same browsing as your own Binders page: search box,
                    filter rail, sort/view, pages. Read-only here; on
                    someone else's profile, cards that fit your binder
                    are highlighted */}
                <div className="profile-binders-content">
                    {currentCards.length === 0 ? (
                        <div className="dash-sec-empty bn-empty">
                            <p>
                                {isOwnProfile ? "You haven't" : `${profileUser.username} hasn't`} added any cards
                                to {isOwnProfile ? 'your' : 'their'} {binderNoun} yet.
                            </p>
                        </div>
                    ) : (
                        <BinderBrowser
                            items={currentCards}
                            entryType={activeTab === 'collection' ? 'have' : 'want'}
                            isHighlighted={isMatched}
                            placeholder={`Search ${isOwnProfile ? 'your' : `${profileUser.username}'s`} ${binderNoun}…`}
                            noun={`${isOwnProfile ? 'your' : 'their'} ${binderNoun}`}
                            resetKey={activeTab}
                            barExtra={!isOwnProfile && (
                                <label className="pro-match-toggle" title="Highlight the cards that fit your binder">
                                    <input
                                        type="checkbox"
                                        checked={showMatches}
                                        onChange={e => setShowMatches(e.target.checked)}
                                    />
                                    <span className="pro-match-switch" aria-hidden="true"><i /></span>
                                    Show matches
                                </label>
                            )}
                        />
                    )}
                </div>
            </div>
            )}

            <ConfirmDialog
                open={blockAsk}
                title={`Block ${profileUser.username}?`}
                message="They can't message you or send trade offers, and you won't see each other in trader lists. Offers still waiting between you are withdrawn; trades already shipping carry on. You can unblock any time."
                confirmLabel="Block"
                danger
                busy={blockBusy}
                onCancel={() => setBlockAsk(false)}
                onConfirm={() => { setBlockAsk(false); toggleBlock(true); }}
            />
        </div>
    );
}

export default Profile;
